import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useUIContext } from "../../../../context/UIContext";
import PreviewQuestion from "./PreviewQuestion";
import styles from "./SurveyPreview.module.css";

export default function SurveyPreview() {
  const { t } = useTranslation();
  const { handleToastCenterTop } = useUIContext();
  const [surveyData, setSurveyData] = useState(null);
  const [responses, setResponses] = useState({});

  useEffect(() => {
    // URL 파라미터에서 설문 데이터 가져오기
    const urlParams = new URLSearchParams(window.location.search);
    const encodedData = urlParams.get("data");
    const previewId = urlParams.get("id");

    if (encodedData) {
      // 기존 방식 (URL 파라미터)
      try {
        const decodedData = JSON.parse(decodeURIComponent(encodedData));
        setSurveyData(decodedData);

        // 응답 초기화
        const initialResponses = {};
        decodedData.questions.forEach((question) => {
          if (question.question_type === "multiple_choice") {
            initialResponses[question.id] = { optionId: "", reason: "" };
          } else if (question.question_type === "checkbox") {
            initialResponses[question.id] = { optionIds: [], reasons: {} };
          } else {
            initialResponses[question.id] = "";
          }
        });
        setResponses(initialResponses);
      } catch (error) {
        console.error("설문 데이터 파싱 오류:", error);
      }
    } else if (previewId) {
      // 새로운 방식 (SessionStorage)
      try {
        const storedData = sessionStorage.getItem(`surveyPreview_${previewId}`);
        if (storedData) {
          const surveyData = JSON.parse(storedData);
          setSurveyData(surveyData);

          // 응답 초기화
          const initialResponses = {};
          surveyData.questions.forEach((question) => {
            if (question.question_type === "multiple_choice") {
              initialResponses[question.id] = { optionId: "", reason: "" };
            } else if (question.question_type === "checkbox") {
              initialResponses[question.id] = { optionIds: [], reasons: {} };
            } else {
              initialResponses[question.id] = "";
            }
          });
          setResponses(initialResponses);

          // 사용 후 삭제 (메모리 절약)
          sessionStorage.removeItem(`surveyPreview_${previewId}`);
        } else {
          console.error(
            "SessionStorage에서 미리보기 데이터를 찾을 수 없습니다."
          );
        }
      } catch (error) {
        console.error("SessionStorage 데이터 파싱 오류:", error);
      }
    }
  }, []);

  const handleResponseChange = (questionId, value) => {
    setResponses((prev) => ({
      ...prev,
      [questionId]: value,
    }));
  };

  const handleSubmit = () => {
    handleToastCenterTop(
      t("common.survey.previewNotSubmitted"),
      3000,
      "#17a2b8"
    );
  };

  const handleClose = () => {
    window.close();
  };

  if (!surveyData) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>{t("common.survey.loadingSurvey")}</p>
      </div>
    );
  }

  const themeClass = styles[surveyData.theme] || styles.classic;

  return (
    <div className={`${styles.previewContainer} ${themeClass}`}>
      {/* 미리보기 헤더 */}
      <div className={styles.previewHeader}>
        <div className={styles.previewBadge}>
          📋 {t("common.survey.previewMode")}
        </div>
        <button className={styles.closeButton} onClick={handleClose}>
          ✕ {t("common.survey.close")}
        </button>
      </div>

      {/* 설문 정보 */}
      <div className={styles.surveyHeader}>
        <h1 className={styles.surveyTitle}>{surveyData.survey.title}</h1>
        {surveyData.description && (
          <p className={styles.surveyDescription}>{surveyData.description}</p>
        )}
        <div className={styles.surveyMeta}>
          <span className={styles.metaItem}>
            📊 {t(`common.survey.${surveyData.survey.survey_type}`)}
          </span>
          <span className={styles.metaItem}>
            👥 {t(`common.survey.${surveyData.survey.response_target}`)}
          </span>
          <span className={styles.metaItem}>
            {surveyData.survey.is_anonymous
              ? "🔒 " + t("common.survey.yes")
              : "👤 " + t("common.survey.no")}{" "}
            {t("common.survey.isAnonymous")}
          </span>
        </div>
      </div>

      {/* 질문 목록 */}
      <form className={styles.surveyForm} onSubmit={(e) => e.preventDefault()}>
        {surveyData.questions.map((question, index) => (
          <PreviewQuestion
            key={question.id}
            question={question}
            index={index}
            value={responses[question.id]}
            onChange={(value) => handleResponseChange(question.id, value)}
            themeStyles={styles}
          />
        ))}

        {/* 제출 버튼 */}
        <div className={styles.submitSection}>
          <button
            type="button"
            className={styles.submitButton}
            onClick={handleSubmit}
          >
            📤 {t("common.survey.previewSubmit")}
          </button>
          <p className={styles.previewNote}>{t("common.survey.previewNote")}</p>
        </div>
      </form>
    </div>
  );
}
